import type { CSSProperties, ReactNode } from 'react';
import { arcPath, GAUGE_END, GAUGE_START, gaugeAngle, polar, type GaugeTone } from './geometry.js';

// the prototype's ring: 260 wide, radius 108, 14 thick, so the setpoint over it has room (prototype "Maschine: …")
const SIZE = 260;
const C = SIZE / 2;
const R = 108;
const STROKE = 14;

/** the heater's bar in the gauge's opening and the two symbols beside it (drawHeaterBar, kStatusSymbolAngle) */
const BAR_FROM = 162;
const BAR_TO = 198;
const SYMBOL_ANGLE = 212.5;
/** the resistor zigzag printed above the Orione's heater lamp, in the display's points (drawHeaterIcon) */
const HEATER_ICON = [
  [-9, 0],
  [-5.5, 0],
  [-4.4, -3.8],
  [-2.2, 3.8],
  [0, -3.8],
  [2.2, 3.8],
  [4.4, -3.8],
  [5.5, 0],
  [9, 0],
];

export type GaugeScale = 'connected' | 'waiting' | 'off';

/**
 * The 270° ring of the round display as an SVG. Decorative (aria-hidden); the centre (children) carries the text.
 * - temperature + setpoint: in the display's scale (drawTemperatureGauge, Dominik 22.10.2026 "ja, beides"): filled
 *   from room temperature up to the tip with a gradient from dark to light, green once ready; above the setpoint
 *   the left half stays full and the part beyond the top shows how far above it is; one dot at the setpoint (since
 *   23.10.2026 instead of a tick per kelvin: "der kreis irgendwie zu kantig"); in the opening at the bottom the
 *   heater's bar with the heater symbol on its left and the scale's on its right; the setpoint over the top while
 *   heating (`top`, prototype)
 * - angle: explicit position (hero, illustrations)
 * - full: closed ring (success)
 * - spinning: rotating segment instead of a value (waiting)
 * - tone idle (standby, no setpoint): the track only, no arc, no marker (prototype "Maschine: Standby")
 * The fill is a conic gradient (registered angles) with round ends: the dark start and a bright tip with a soft glow,
 * which turns around the centre, so both glide to a new value instead of jumping (Dominik 21.10.2026: "laggy"): over
 * a second, the pace of the live values, or with `follow="frame"` in a few frames when the caller already moves the
 * value every frame (the reveal's count-up).
 */
export function Gauge({
  temperature,
  setpoint,
  angle,
  tone = 'idle',
  full = false,
  spinning = false,
  ticks = true,
  top,
  follow = 'sample',
  heater = null,
  scale = null,
  pulse = 0,
  className,
  children,
}: {
  temperature?: number | null;
  setpoint?: number | null;
  angle?: number;
  tone?: GaugeTone;
  full?: boolean;
  spinning?: boolean;
  ticks?: boolean;
  /** the setpoint over the ring's top ("93°") */
  top?: string | null;
  /** how the drawn value follows a new one: over a live value's second, or within a few frames */
  follow?: 'sample' | 'frame';
  /** heater output in percent for the bar in the opening; null: no bar */
  heater?: number | null;
  /** the scale's symbol at the right of the bar: connected (green), waiting (grey), off (crossed out) */
  scale?: GaugeScale | null;
  /** the ready moment of the display: one soft ring spreads from the tip (a new number plays it once) */
  pulse?: number;
  className?: string;
  children?: ReactNode;
}) {
  const hasValue = typeof temperature === 'number' && typeof setpoint === 'number';
  const a = angle ?? (hasValue ? gaugeAngle(temperature, setpoint) : null);
  const idle = tone === 'idle';
  const shown = a !== null && !full && !spinning && !idle;
  // the temperature in the display's scale: gradient fill, the setpoint's dot, the heater's bar
  const display = hasValue && angle === undefined && !full && !spinning;
  const head = a === null ? GAUGE_START : Math.min(a, 0);
  const fill = {
    '--g-a': `${Math.max(0, head - GAUGE_START)}deg`,
    '--g-b': `${a !== null && a > 0 ? a : 0}deg`,
  } as CSSProperties;
  // the setpoint's dot for the other uses too
  const plainTick = ticks && !full && !display && (hasValue || angle !== undefined);
  const dot = polar(C, C, R - 17, 0);
  const share = a === null ? 0 : Math.min(1, Math.max(0, (a - GAUGE_START) / (GAUGE_END - GAUGE_START)));
  const heat = heater === null ? 0 : Math.min(1, Math.max(0, heater / 100));
  return (
    <div className={`gauge gauge-${tone} is-follow-${follow}${className ? ` ${className}` : ''}`}>
      <svg className="gauge-svg" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true" focusable="false">
        {full ? (
          <circle className="gauge-track" cx={C} cy={C} r={R} strokeWidth={STROKE} />
        ) : (
          <path className="gauge-track" d={arcPath(C, C, R, GAUGE_START, GAUGE_END)} strokeWidth={STROKE} />
        )}
        {(plainTick || (display && !idle && setpoint !== null && setpoint !== undefined)) && (
          <circle className="gauge-setpoint" cx={dot.x} cy={dot.y} r={2.5} />
        )}
        {display && !idle && heater !== null && (
          <>
            <path className="gauge-bar" d={arcPath(C, C, R, BAR_FROM, BAR_TO)} />
            {/* drawn from the left end, so the dash starts where the bar fills from (no dot of an empty dash) */}
            <path
              className="gauge-bar is-value"
              d={barFromLeft()}
              pathLength={1}
              style={{ strokeDasharray: `${heat} 1`, opacity: heat > 0.005 ? 1 : 0 }}
            />
            <path className="gauge-symbol" d={heaterIcon()} />
            {scale && <path className={`gauge-symbol is-scale is-${scale}`} d={scaleIcon(scale === 'off')} />}
          </>
        )}
        {display && shown && head - GAUGE_START > 0.5 && (
          <circle
            className="gauge-cap"
            cx={polar(C, C, R, GAUGE_START).x}
            cy={polar(C, C, R, GAUGE_START).y}
            r={STROKE / 2}
          />
        )}
        {full && <circle className="gauge-value" cx={C} cy={C} r={R} strokeWidth={STROKE} />}
        {!full && !display && (
          <path
            className="gauge-value is-dash"
            d={arcPath(C, C, R, GAUGE_START, GAUGE_END)}
            pathLength={1}
            strokeWidth={STROKE}
            style={{ strokeDashoffset: 1 - share, opacity: shown && share > 0.002 ? 1 : 0 }}
          />
        )}
        {spinning && (
          <g className="gauge-spin">
            <path className="gauge-value" d={arcPath(C, C, R, -30, 30)} strokeWidth={STROKE} />
          </g>
        )}
      </svg>
      {display && (
        <>
          <div className={shown ? 'gauge-fill' : 'gauge-fill is-off'} style={fill} />
          <div className={shown && a !== null && a > 0 ? 'gauge-over' : 'gauge-over is-off'} style={fill} />
        </>
      )}
      {!full && !spinning && (
        <span
          className={
            display &&
            typeof temperature === 'number' &&
            typeof setpoint === 'number' &&
            temperature < setpoint - 5
              ? 'gauge-tip is-warmup'
              : 'gauge-tip'
          }
          style={{ transform: `rotate(${a ?? GAUGE_START}deg)`, opacity: shown ? 1 : 0 }}
        >
          {pulse > 0 && <span key={pulse} className="gauge-ripple" />}
        </span>
      )}
      {top && (
        <span className="gauge-top num" aria-hidden="true">
          {top}
        </span>
      )}
      {children !== undefined && <div className="gauge-center">{children}</div>}
    </div>
  );
}

/** The heater's bar from its left end (198°) to its right one (162°), against the clock. */
function barFromLeft(): string {
  const a = polar(C, C, R, BAR_TO);
  const b = polar(C, C, R, BAR_FROM);
  return `M ${a.x} ${a.y} A ${R} ${R} 0 0 0 ${b.x} ${b.y}`;
}

/** the heater symbol at the left of the opening, 1.1 times the display's points */
function heaterIcon(): string {
  const o = polar(C, C, R, SYMBOL_ANGLE);
  return HEATER_ICON.map(
    ([x, y], i) => `${i ? 'L' : 'M'}${round(o.x + (x as number) * 1.1)} ${round(o.y + (y as number) * 1.1)}`,
  ).join('');
}

/** the scale symbol at the right (drawScaleIcon): platform, base, a small display; crossed out without a scale */
function scaleIcon(crossed: boolean): string {
  const o = polar(C, C, R, 360 - SYMBOL_ANGLE);
  const s = 1.1;
  const at = (x: number, y: number) => `${round(o.x + x * s)} ${round(o.y + y * s)}`;
  return (
    `M${at(-9, -3.8)}L${at(9, -3.8)}` +
    `M${at(-7, -1)}L${at(7, -1)}L${at(7, 3.8)}L${at(-7, 3.8)}Z` +
    `M${at(1.5, 1.4)}L${at(4, 1.4)}` +
    (crossed ? `M${at(-8, 4.6)}L${at(8, -4.6)}` : '')
  );
}

const round = (x: number) => Math.round(x * 100) / 100;
